/**
 * SEO utilities for Asymmetry Edits.
 * Single source of truth for per-route head updates and structured data templates.
 * No external dependency (avoids react-helmet-async). Works for SPA → updates document head on route change.
 */

export const SITE_URL = "https://asymmetryedits.com";
export const BRAND = "Asymmetry Edits";
export const SUB_BRAND = "DenimGlow";
export const DEFAULT_OG_IMAGE = `${SITE_URL}/og-cover.jpg`;

/** Set or replace a meta tag by name or property. */
function setMeta(attr, key, value) {
  if (value === undefined || value === null) return;
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", value);
}

function setLink(rel, href, attrs = {}) {
  if (!href) return;
  const selector = `link[rel="${rel}"]` + (attrs.hreflang ? `[hreflang="${attrs.hreflang}"]` : "");
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
  Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
}

function setJsonLd(id, data) {
  let el = document.getElementById(id);
  if (!el) {
    el = document.createElement("script");
    el.type = "application/ld+json";
    el.id = id;
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
}

function removeJsonLd(id) {
  const el = document.getElementById(id);
  if (el && el.parentNode) el.parentNode.removeChild(el);
}

/**
 * Apply per-page SEO.
 * opts: { title, description, canonical, image, og: {type}, robots, jsonLd: [{id, data}] }
 */
export function applySEO(opts = {}) {
  const title = opts.title || `${BRAND} — Luxury Fashion House · DenimGlow Couture`;
  const description = opts.description || `A global luxury fashion house. Designer denim, jewellery, handbags, footwear and accessories — hand-finished for the bold.`;
  const canonical = opts.canonical || (typeof window !== "undefined" ? `${SITE_URL}${window.location.pathname}` : SITE_URL + "/");
  const image = opts.image || DEFAULT_OG_IMAGE;
  const ogType = opts.og?.type || "website";
  const robots = opts.robots || "index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1";

  document.title = title;
  setMeta("name", "description", description);
  setMeta("name", "robots", robots);
  setLink("canonical", canonical);

  // Open Graph
  setMeta("property", "og:title", title);
  setMeta("property", "og:description", description);
  setMeta("property", "og:url", canonical);
  setMeta("property", "og:image", image);
  setMeta("property", "og:type", ogType);
  setMeta("property", "og:site_name", BRAND);

  // Twitter
  setMeta("name", "twitter:card", "summary_large_image");
  setMeta("name", "twitter:title", title);
  setMeta("name", "twitter:description", description);
  setMeta("name", "twitter:image", image);

  // Structured data
  const ids = (opts.jsonLd || []).map(({ id, data }) => {
    setJsonLd(id, data);
    return id;
  });
  // Clean up any previous per-page jsonld not in new set
  document.querySelectorAll('script[type="application/ld+json"][data-dynamic="true"]').forEach((el) => {
    if (!ids.includes(el.id)) el.remove();
  });
  ids.forEach((id) => {
    const el = document.getElementById(id);
    if (el) el.setAttribute("data-dynamic", "true");
  });
}

/* ---------------- Schema templates ---------------- */

export function breadcrumbSchema(items) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((it, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: it.name,
      item: it.url ? (it.url.startsWith("http") ? it.url : `${SITE_URL}${it.url}`) : undefined,
    })),
  };
}

export function productSchema(p, currency = "INR") {
  if (!p) return null;
  const price = currency === "INR" ? p.price_inr : p.price_usd;
  const availability = (p.stock_remaining ?? p.stock ?? 0) > 0
    ? "https://schema.org/InStock"
    : "https://schema.org/OutOfStock";
  return {
    "@context": "https://schema.org",
    "@type": "Product",
    "@id": `${SITE_URL}/product/${p.id}#product`,
    name: `${p.name} — ${BRAND}`,
    description: p.description || p.tagline,
    sku: p.sku || p.id,
    productID: p.id,
    image: (p.images && p.images.length ? p.images : [p.image]).map((u) => (u?.startsWith("http") ? u : `${SITE_URL}${u || ""}`)),
    brand: { "@type": "Brand", name: BRAND },
    category: p.category,
    material: (p.materials || []).join(", ") || undefined,
    offers: {
      "@type": "Offer",
      "@id": `${SITE_URL}/product/${p.id}#offer`,
      url: `${SITE_URL}/product/${p.id}`,
      priceCurrency: currency,
      price: price,
      availability,
      itemCondition: "https://schema.org/NewCondition",
      seller: { "@type": "Organization", name: BRAND },
      hasMerchantReturnPolicy: {
        "@type": "MerchantReturnPolicy",
        applicableCountry: ["IN", "US", "GB"],
        returnPolicyCategory: "https://schema.org/MerchantReturnNotPermitted",
      },
    },
  };
}

export function collectionPageSchema({ url, name, description }) {
  return {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "@id": `${SITE_URL}${url}#collection`,
    url: `${SITE_URL}${url}`,
    name,
    description,
    isPartOf: { "@id": `${SITE_URL}/#website` },
    publisher: { "@id": `${SITE_URL}/#organization` },
  };
}

export function faqSchema(qa) {
  if (!qa || qa.length === 0) return null;
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: qa.map((p) => ({
      "@type": "Question",
      name: p.q,
      acceptedAnswer: { "@type": "Answer", text: p.a },
    })),
  };
}

export function reviewSchema(productId, productName, reviews) {
  if (!reviews || reviews.length === 0) return null;
  const agg = reviews.reduce((s, r) => s + r.rating, 0) / reviews.length;
  return {
    "@context": "https://schema.org",
    "@type": "Product",
    "@id": `${SITE_URL}/product/${productId}#product`,
    name: productName,
    aggregateRating: {
      "@type": "AggregateRating",
      ratingValue: agg.toFixed(1),
      reviewCount: reviews.length,
      bestRating: "5",
      worstRating: "1",
    },
    review: reviews.map((r) => ({
      "@type": "Review",
      reviewRating: { "@type": "Rating", ratingValue: r.rating, bestRating: "5" },
      author: { "@type": "Person", name: r.author || "Verified buyer" },
      reviewBody: r.body,
      datePublished: r.date,
    })),
  };
}

/* ---------------- Image alt + filename helpers (future product uploads) ---------------- */
export function productImageAlt(product, index = 0) {
  if (!product) return "Asymmetry Edits";
  const cat = product.category || "edit";
  return `${product.name} — ${cat} by ${BRAND}${index ? ` (view ${index + 1})` : ""}`;
}

export function slugify(s) {
  return (s || "")
    .toString()
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "")
    .slice(0, 64);
}

export function productImageFilename(sku, name, index, ext = "jpg") {
  const parts = [sku, slugify(name), String(index || 1).padStart(2, "0")].filter(Boolean);
  return `${parts.join("-")}.${ext}`;
}
